import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardWrapper from "../../components/layout/DashboardWrapper";
import { fetchOrganisationsWithCounts } from "../../services/orgService";
import { OrganisationWithCounts, OwnerDocIssue, ATHLETE_SUBSCRIPTION_LABELS, resolveAthleteSubscription } from "../../types/organisation";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { db } from "../../firebase/config";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
    MagnifyingGlassIcon,
    ArrowPathIcon,
    BuildingOffice2Icon,
    ExclamationTriangleIcon,
    PlusIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";

const toDateOrNull = (value: unknown): Date | null => {
    const date = typeof (value as any)?.toDate === "function"
        ? (value as any).toDate()
        : value instanceof Date ? value : null;
    return date;
};

const formatDate = (value: unknown): string => {
    const date = toDateOrNull(value);
    if (!date) return "—";
    return date.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
};

const toMillis = (value: unknown): number => toDateOrNull(value)?.getTime() ?? 0;

type SortField = "name" | "owners" | "coaches" | "athletes" | "updatedAt" | "createdAt";
type SortDirection = "asc" | "desc";

const SORT_FIELD_TYPE: Record<SortField, "string" | "number"> = {
    name: "string",
    owners: "number",
    coaches: "number",
    athletes: "number",
    updatedAt: "number",
    createdAt: "number",
};

const formatSubscriptionLabel = (subscriptionType: string, subscriptionVia: string): string => {
    const type = subscriptionType.trim() || "default";
    const via = subscriptionVia.trim() || "default";
    return type === "default" ? "default" : `${type} via ${via}`;
};

const hasCoachViaStripeSubscription = (labels: string[]): boolean =>
    labels.some((label) => label.trim().toLowerCase() === "coach via stripe");

type OrgFilter = "all" | "individual" | "team" | "commercial" | "ownerIssue";

const OWNER_ISSUE_TITLE: Record<OwnerDocIssue, string> = {
    missing: "The organisation's ownerId has no owners doc, so that user will lose owner access. Open the organisation to restore it.",
    inactive: "The organisation's ownerId has an inactive owners doc, so that user will lose owner access. Open the organisation to restore it.",
};

type RecheckStatus = "checking" | "success" | "failed";

interface RecheckEntry {
    id: string;
    orgId: string;
    orgName: string;
    role: "owner" | "athlete";
    userId: string;
    status: RecheckStatus;
    error?: string;
}

const OrganisationsPage: React.FC = () => {
    const navigate = useNavigate();
    const [organisations, setOrganisations] = useState<OrganisationWithCounts[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [orgFilter, setOrgFilter] = useState<OrgFilter>("all");
    const [recheckOpen, setRecheckOpen] = useState(false);
    const [rechecking, setRechecking] = useState(false);
    const [recheckEntries, setRecheckEntries] = useState<RecheckEntry[]>([]);
    const [processedOrgs, setProcessedOrgs] = useState(0);
    const [ownerSubscriptionsByOrg, setOwnerSubscriptionsByOrg] = useState<Record<string, string[]>>({});
    const [sortField, setSortField] = useState<SortField | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
            return;
        }
        setSortField(field);
        setSortDirection(SORT_FIELD_TYPE[field] === "string" ? "asc" : "desc");
    };

    const renderSortIcon = (field: SortField) => (
        <span className={`ml-1 inline-block ${sortField === field ? "text-gray-700" : "text-gray-300"}`}>
            {sortField === field && sortDirection === "desc" ? "▼" : "▲"}
        </span>
    );

    const fetchOwnerSubscriptions = useCallback(async (orgs: OrganisationWithCounts[]) => {
        if (orgs.length === 0) {
            setOwnerSubscriptionsByOrg({});
            return;
        }

        try {
            const rows = await Promise.all(
                orgs.map(async (org) => {
                    const ownersSnap = await getDocs(collection(db, "organisations", org.id, "owners"));
                    const ownerIds = Array.from(
                        new Set([
                            ...ownersSnap.docs.map((docSnap) => docSnap.id),
                            ...(org.ownerId ? [org.ownerId] : []),
                        ])
                    );

                    const labels = await Promise.all(
                        ownerIds.map(async (ownerId) => {
                            try {
                                const activeSubscriptionRef = doc(db, "activeSubscriptions", ownerId);
                                const activeSubscriptionDoc = await getDoc(activeSubscriptionRef);

                                if (!activeSubscriptionDoc.exists()) return "default";
                                const data = activeSubscriptionDoc.data() as Record<string, unknown>;
                                const subscriptionType =
                                    typeof data.subscriptionType === "string" ? data.subscriptionType : "default";
                                const subscriptionVia =
                                    typeof data.subscriptionVia === "string" ? data.subscriptionVia : "default";

                                return formatSubscriptionLabel(subscriptionType, subscriptionVia);
                            } catch (error) {
                                console.error(`[OrganisationsPage] Failed to fetch owner subscription: ${ownerId}`, error);
                                return "default";
                            }
                        })
                    );

                    return [org.id, labels] as const;
                })
            );

            setOwnerSubscriptionsByOrg(Object.fromEntries(rows));
        } catch (error) {
            console.error("[OrganisationsPage] Failed to fetch owner subscriptions:", error);
            setOwnerSubscriptionsByOrg({});
        }
    }, []);

    const fetchOrganisations = useCallback(async () => {
        setLoading(true);
        try {
            const orgs = await fetchOrganisationsWithCounts();
            setOrganisations(orgs);
            void fetchOwnerSubscriptions(orgs);
        } catch (err) {
            console.error("[OrganisationsPage] Failed to fetch:", err);
        } finally {
            setLoading(false);
        }
    }, [fetchOwnerSubscriptions]);

    useEffect(() => {
        fetchOrganisations();
    }, [fetchOrganisations]);

    const filtered = organisations
        .filter((org) => {
            if (orgFilter === "ownerIssue") {
                if (!org.ownerDocIssue) return false;
            } else if (orgFilter !== "all" && org.config?.orgType !== orgFilter) {
                return false;
            }
            const q = search.toLowerCase();
            return !q || org.name.toLowerCase().includes(q);
        })
        .sort((a, b) => {
            if (sortField) {
                let cmp = 0;
                switch (sortField) {
                    case "name":
                        cmp = a.name.localeCompare(b.name);
                        break;
                    case "owners":
                        cmp = a.ownerCount - b.ownerCount;
                        break;
                    case "coaches":
                        cmp = a.coachCount - b.coachCount;
                        break;
                    case "athletes":
                        cmp = a.athleteCount - b.athleteCount;
                        break;
                    case "updatedAt":
                        cmp = toMillis(a.updatedAt) - toMillis(b.updatedAt);
                        break;
                    case "createdAt":
                        cmp = toMillis(a.createdAt) - toMillis(b.createdAt);
                        break;
                }
                return sortDirection === "asc" ? cmp : -cmp;
            }

            const aIsCommercial = a.config?.orgType === "commercial";
            const bIsCommercial = b.config?.orgType === "commercial";
            const aHasCoachViaStripe = hasCoachViaStripeSubscription(ownerSubscriptionsByOrg[a.id] ?? []);
            const bHasCoachViaStripe = hasCoachViaStripeSubscription(ownerSubscriptionsByOrg[b.id] ?? []);

            const getPriority = (isCommercial: boolean, hasCoachViaStripe: boolean): number => {
                if (isCommercial) return 0;
                if (hasCoachViaStripe) return 1;
                return 2;
            };

            const priorityDiff =
                getPriority(aIsCommercial, aHasCoachViaStripe) -
                getPriority(bIsCommercial, bHasCoachViaStripe);

            if (priorityDiff !== 0) return priorityDiff;
            if (b.athleteCount !== a.athleteCount) return b.athleteCount - a.athleteCount;
            return a.name.localeCompare(b.name);
        });

    const runSubscriptionRecheck = useCallback(async () => {
        if (rechecking || organisations.length === 0) return;

        setRecheckOpen(true);
        setRechecking(true);
        setRecheckEntries([]);
        setProcessedOrgs(0);

        const triggerCheckUserSubscription = httpsCallable(getFunctions(), "triggerCheckUserSubscription");
        let entryCounter = 0;

        const runUserCheck = async (
            orgId: string,
            orgName: string,
            role: "owner" | "athlete",
            userId: string
        ) => {
            const entryId = `${orgId}-${role}-${userId}-${entryCounter++}`;
            setRecheckEntries((prev) => [
                ...prev,
                {
                    id: entryId,
                    orgId,
                    orgName,
                    role,
                    userId,
                    status: "checking",
                },
            ]);

            try {
                await triggerCheckUserSubscription({ userId });
                setRecheckEntries((prev) =>
                    prev.map((entry) =>
                        entry.id === entryId ? { ...entry, status: "success" } : entry
                    )
                );
            } catch (error) {
                const message =
                    error instanceof Error
                        ? error.message
                        : "Failed to recheck subscription";

                setRecheckEntries((prev) =>
                    prev.map((entry) =>
                        entry.id === entryId
                            ? { ...entry, status: "failed", error: message }
                            : entry
                    )
                );
            }
        };

        try {
            for (const org of organisations) {
                const [ownersSnap, membersSnap] = await Promise.all([
                    getDocs(collection(db, "organisations", org.id, "owners")),
                    getDocs(collection(db, "organisations", org.id, "members")),
                ]);

                const ownerIds = Array.from(
                    new Set([
                        ...ownersSnap.docs.map((docSnap) => docSnap.id),
                        ...(org.ownerId ? [org.ownerId] : []),
                    ])
                );
                const athleteIds = Array.from(new Set(membersSnap.docs.map((docSnap) => docSnap.id)));

                for (const ownerId of ownerIds) {
                    await runUserCheck(org.id, org.name, "owner", ownerId);
                }

                for (const athleteId of athleteIds) {
                    await runUserCheck(org.id, org.name, "athlete", athleteId);
                }

                setProcessedOrgs((prev) => prev + 1);
            }
        } catch (error) {
            console.error("[OrganisationsPage] Subscription recheck failed:", error);
        } finally {
            setRechecking(false);
        }
    }, [organisations, rechecking]);

    const totalChecks = recheckEntries.length;
    const checkingCount = recheckEntries.filter((entry) => entry.status === "checking").length;
    const successCount = recheckEntries.filter((entry) => entry.status === "success").length;
    const failedCount = recheckEntries.filter((entry) => entry.status === "failed").length;
    const ownerIssueCount = organisations.filter((org) => org.ownerDocIssue).length;

    return (
        <DashboardWrapper>
            <div className="p-6 flex flex-col gap-6 min-h-[calc(100vh-64px)]">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Organisations</h1>
                        <p className="text-sm text-gray-500 mt-0.5">
                            {organisations.length} organisation{organisations.length !== 1 ? "s" : ""} total
                            {orgFilter !== "all" && ` · ${filtered.length} shown`}
                        </p>
                        {!loading && ownerIssueCount > 0 && (
                            <button
                                type="button"
                                onClick={() => setOrgFilter("ownerIssue")}
                                className="mt-2 inline-flex items-center gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 hover:bg-amber-100"
                            >
                                <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                                {ownerIssueCount} organisation{ownerIssueCount !== 1 ? "s have" : " has"} an owner without an active owners doc
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={fetchOrganisations}
                            className="btn btn-ghost btn-sm gap-2"
                            title="Refresh"
                        >
                            <ArrowPathIcon className="h-4 w-4" />
                            Refresh
                        </button>
                        <button
                            onClick={runSubscriptionRecheck}
                            className="btn btn-ghost btn-sm gap-2"
                            title="Recheck subscriptions"
                            disabled={loading || rechecking || organisations.length === 0}
                        >
                            <ArrowPathIcon className={`h-4 w-4 ${rechecking ? "animate-spin" : ""}`} />
                            Recheck Subscriptions
                        </button>
                        <Link
                            to="/commercial/organisations/new"
                            className="btn btn-sm gap-2 bg-gray-900 text-white hover:bg-gray-700 border-none"
                        >
                            <PlusIcon className="h-4 w-4" />
                            New Organisation
                        </Link>
                    </div>
                </div>

                {/* Search + Filter */}
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="relative max-w-md flex-1">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by name…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="input input-bordered input-sm w-full pl-9"
                        />
                    </div>
                    <select
                        value={orgFilter}
                        onChange={(e) => setOrgFilter(e.target.value as OrgFilter)}
                        className="select select-bordered select-sm"
                    >
                        <option value="all">All organisations</option>
                        <option value="individual">Individual</option>
                        <option value="team">Team</option>
                        <option value="commercial">Commercial</option>
                        <option value="ownerIssue">Owner access issues</option>
                    </select>
                </div>

                {/* Table */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col flex-1">
                    {loading ? (
                        <div className="flex-1 flex items-center justify-center py-24">
                            <span className="loading loading-spinner loading-md text-gray-400" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-gray-400 py-24">
                            <BuildingOffice2Icon className="h-10 w-10" />
                            <p className="text-sm">No organisations found</p>
                        </div>
                    ) : (
                        <div className="overflow-auto">
                            <table className="table table-sm w-full">
                                <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                                    <tr>
                                        <th
                                            className="font-medium py-3 px-4 cursor-pointer select-none hover:text-gray-700"
                                            onClick={() => handleSort("name")}
                                        >
                                            Organisation{renderSortIcon("name")}
                                        </th>
                                        <th
                                            className="font-medium py-3 px-4 hidden md:table-cell cursor-pointer select-none hover:text-gray-700"
                                            onClick={() => handleSort("owners")}
                                        >
                                            Owners{renderSortIcon("owners")}
                                        </th>
                                        <th
                                            className="font-medium py-3 px-4 hidden md:table-cell cursor-pointer select-none hover:text-gray-700"
                                            onClick={() => handleSort("coaches")}
                                        >
                                            Coaches{renderSortIcon("coaches")}
                                        </th>
                                        <th
                                            className="font-medium py-3 px-4 hidden lg:table-cell cursor-pointer select-none hover:text-gray-700"
                                            onClick={() => handleSort("athletes")}
                                        >
                                            Athletes{renderSortIcon("athletes")}
                                        </th>
                                        <th className="font-medium py-3 px-4 hidden xl:table-cell">Org Type</th>
                                        <th className="font-medium py-3 px-4 hidden xl:table-cell">Status</th>
                                        <th
                                            className="font-medium py-3 px-4 hidden lg:table-cell cursor-pointer select-none hover:text-gray-700"
                                            onClick={() => handleSort("updatedAt")}
                                        >
                                            Last Updated{renderSortIcon("updatedAt")}
                                        </th>
                                        <th
                                            className="font-medium py-3 px-4 hidden sm:table-cell cursor-pointer select-none hover:text-gray-700"
                                            onClick={() => handleSort("createdAt")}
                                        >
                                            Created{renderSortIcon("createdAt")}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {filtered.map((org) => (
                                        (() => {
                                            return (
                                        <tr
                                            key={org.id}
                                            onClick={() => navigate(`/commercial/organisations/${org.id}`)}
                                            className="hover:bg-gray-50 transition-colors cursor-pointer"
                                        >
                                            <td className="py-3 px-4">
                                                <div className="flex items-center gap-3" title={org.id}>
                                                    {org.imageUrl ? (
                                                        <img
                                                            src={org.imageUrl}
                                                            alt={org.name}
                                                            className="h-8 w-8 rounded-lg object-cover flex-shrink-0"
                                                        />
                                                    ) : (
                                                        <div className="h-8 w-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                                            <BuildingOffice2Icon className="h-4 w-4 text-gray-400" />
                                                        </div>
                                                    )}
                                                    <span className="font-medium text-gray-900 text-sm">
                                                        {org.name}
                                                    </span>
                                                    {org.ownerDocIssue && (
                                                        <span
                                                            className="badge badge-sm gap-1 bg-amber-100 text-amber-800 border-amber-200"
                                                            title={OWNER_ISSUE_TITLE[org.ownerDocIssue]}
                                                        >
                                                            <ExclamationTriangleIcon className="h-3 w-3" />
                                                            Owner doc {org.ownerDocIssue}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 hidden md:table-cell text-sm text-gray-600">
                                                <div className="py-1 px-2 rounded-lg bg-gray-100 inline-flex items-center gap-1">
                                                    {org.ownerCount}
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 hidden md:table-cell text-sm text-gray-600">
                                                <div className="py-1 px-2 rounded-lg bg-gray-100 inline-flex items-center gap-1">
                                                    {org.coachCount}
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 hidden lg:table-cell text-sm text-gray-600">
                                                <div className="py-1 px-2 rounded-lg bg-gray-100 inline-flex items-center gap-1">
                                                    {org.athleteCount}
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 hidden xl:table-cell text-sm text-gray-600">
                                                {org.config?.orgType ? (
                                                    <div className="flex flex-col items-start gap-1">
                                                        <span className="badge badge-outline text-xs capitalize">{org.config.orgType}</span>
                                                        {org.config.orgType === "commercial" && (
                                                            <span className="text-xs text-gray-500">
                                                                Athletes: {ATHLETE_SUBSCRIPTION_LABELS[resolveAthleteSubscription(org.config.athleteSubscription)]}
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-gray-400">—</span>
                                                )}
                                            </td>
                                            <td className="py-3 px-4 hidden xl:table-cell text-sm text-gray-600">
                                                {org.config?.status ? (
                                                    <span className={`badge badge-outline text-xs capitalize ${org.config.status === "active" ? "text-green-700" : org.config.status === "trial" ? "text-blue-700" : org.config.status === "pastDue" ? "text-yellow-700" : org.config.status === "paused" ? "text-gray-700" : org.config.status === "canceled" ? "text-red-700" : ""}`}>
                                                        {org.config.status}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-gray-400">—</span>
                                                )}
                                            </td>
                                            <td className="py-3 px-4 hidden lg:table-cell text-sm text-gray-500">
                                                {formatDate(org.updatedAt)}
                                            </td>
                                            <td className="py-3 px-4 hidden sm:table-cell text-sm text-gray-500">
                                                {formatDate(org.createdAt)}
                                            </td>
                                        </tr>
                                            );
                                        })()
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {recheckOpen && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-4xl">
                        <div className="flex items-start justify-between gap-4 mb-4">
                            <div>
                                <h3 className="text-lg font-semibold">Recheck Subscriptions</h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    Rechecking owners and athletes across all organisations.
                                </p>
                            </div>
                            <button
                                className="btn btn-sm btn-circle btn-ghost"
                                onClick={() => {
                                    if (!rechecking) {
                                        setRecheckOpen(false);
                                    }
                                }}
                                disabled={rechecking}
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                            <div className="rounded-lg border p-3 bg-gray-50">
                                <p className="text-xs uppercase text-gray-500">Organisations</p>
                                <p className="text-sm font-semibold">
                                    {processedOrgs}/{organisations.length}
                                </p>
                            </div>
                            <div className="rounded-lg border p-3 bg-gray-50">
                                <p className="text-xs uppercase text-gray-500">Total checks</p>
                                <p className="text-sm font-semibold">{totalChecks}</p>
                            </div>
                            <div className="rounded-lg border p-3 bg-gray-50">
                                <p className="text-xs uppercase text-gray-500">Successful</p>
                                <p className="text-sm font-semibold text-green-700">{successCount}</p>
                            </div>
                            <div className="rounded-lg border p-3 bg-gray-50">
                                <p className="text-xs uppercase text-gray-500">Failed</p>
                                <p className="text-sm font-semibold text-red-700">
                                    {failedCount}
                                    {checkingCount > 0 ? ` (${checkingCount} checking)` : ""}
                                </p>
                            </div>
                        </div>

                        <div className="border rounded-lg max-h-96 overflow-auto">
                            <table className="table table-sm w-full">
                                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                                    <tr>
                                        <th>Organisation</th>
                                        <th>Role</th>
                                        <th>User</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recheckEntries.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="text-center text-gray-500 py-6">
                                                Starting recheck...
                                            </td>
                                        </tr>
                                    ) : (
                                        recheckEntries.map((entry) => (
                                            <tr key={entry.id}>
                                                <td className="text-sm">{entry.orgName}</td>
                                                <td className="text-sm capitalize">{entry.role}</td>
                                                <td className="text-xs font-mono">{entry.userId}</td>
                                                <td>
                                                    {entry.status === "checking" && (
                                                        <span className="badge badge-ghost badge-sm">Checking</span>
                                                    )}
                                                    {entry.status === "success" && (
                                                        <span className="badge badge-success badge-sm text-white">Success</span>
                                                    )}
                                                    {entry.status === "failed" && (
                                                        <div className="flex flex-col gap-1">
                                                            <span className="badge badge-error badge-sm text-white">Failed</span>
                                                            {entry.error && (
                                                                <span className="text-[11px] text-red-600">{entry.error}</span>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="modal-action">
                            <button
                                className="btn btn-ghost"
                                onClick={() => setRecheckOpen(false)}
                                disabled={rechecking}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => {
                            if (!rechecking) {
                                setRecheckOpen(false);
                            }
                        }}
                    />
                </div>
            )}
        </DashboardWrapper>
    );
};

export default OrganisationsPage;
